import { useEffect, useRef, useState } from 'react';
import { api, ErrorApi, type Sesion } from '../api';

/**
 * Pantalla del segundo paso del inicio de sesión.
 *
 * Acepta tanto el código de la aplicación como uno de respaldo, en el mismo
 * campo. Obligar a elegir «tengo el teléfono» o «no lo tengo» agrega una
 * decisión justo cuando la persona está apurada por entrar, y el servidor
 * distingue solo con mirar el formato.
 */
export function PedirSegundoFactor({
  onListo,
  onCancelar,
}: {
  onListo: (sesion: Sesion) => void;
  onCancelar: () => void;
}) {
  const [codigo, setCodigo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function enviar(evento: React.FormEvent): Promise<void> {
    evento.preventDefault();
    if (!codigo.trim()) return;

    setEnviando(true);
    setError(null);
    try {
      onListo(await api.verificarSegundoFactor(codigo.trim()));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'no se pudo contactar el portal');
      setCodigo('');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="acceso">
      <h1>Verificación en dos pasos</h1>
      <p>Ingresa el código de tu aplicación de autenticación.</p>

      <form onSubmit={(e) => void enviar(e)}>
        <label>
          <span>Código</span>
          <input
            type="text"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            autoFocus
            required
          />
        </label>

        <button type="submit" disabled={enviando}>
          {enviando ? 'Verificando…' : 'Verificar'}
        </button>
      </form>

      {error && <p className="error">{error}</p>}

      <p className="aviso-seguridad">
        ¿Perdiste el teléfono? Escribe uno de tus códigos de respaldo en el mismo campo.
      </p>

      <button className="enlace bajo" onClick={onCancelar}>
        Volver al acceso
      </button>
    </div>
  );
}

/**
 * Alta y baja del segundo factor, dentro del diálogo de seguridad.
 *
 * El alta es de dos pasos a propósito: primero se muestra el código QR y
 * recién queda activo cuando la persona demuestra que su aplicación produce un
 * código válido. Activarlo antes dejaría fuera de la cuenta a quien copió mal
 * el secreto o tiene el reloj desfasado.
 */
export function AdministrarSegundoFactor({
  activo,
  codigosRestantes,
  onCambio,
}: {
  activo: boolean;
  codigosRestantes: number;
  onCambio: () => void;
}) {
  const [paso, setPaso] = useState<'inicio' | 'confirmar' | 'codigos' | 'desactivar'>('inicio');
  const [alta, setAlta] = useState<{ secreto: string; qr: string } | null>(null);
  const [codigo, setCodigo] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [respaldo, setRespaldo] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const primerCampo = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (paso === 'confirmar' || paso === 'desactivar') primerCampo.current?.focus();
  }, [paso]);

  async function iniciar(): Promise<void> {
    setOcupado(true);
    setError(null);
    try {
      const datos = await api.iniciarSegundoFactor();
      setAlta({ secreto: datos.secreto, qr: datos.qr });
      setPaso('confirmar');
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'no se pudo iniciar');
    } finally {
      setOcupado(false);
    }
  }

  async function activar(evento: React.FormEvent): Promise<void> {
    evento.preventDefault();
    setOcupado(true);
    setError(null);
    try {
      const datos = await api.activarSegundoFactor(codigo.trim());
      setRespaldo(datos.codigosRespaldo);
      setPaso('codigos');
      setCodigo('');
      onCambio();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'no se pudo activar');
      setCodigo('');
    } finally {
      setOcupado(false);
    }
  }

  async function desactivar(evento: React.FormEvent): Promise<void> {
    evento.preventDefault();
    setOcupado(true);
    setError(null);
    try {
      await api.desactivarSegundoFactor(contrasena, codigo.trim());
      setPaso('inicio');
      setContrasena('');
      setCodigo('');
      onCambio();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'no se pudo desactivar');
      setCodigo('');
    } finally {
      setOcupado(false);
    }
  }

  if (paso === 'codigos') {
    return (
      <div className="bloque-seguridad">
        <h3>Guarda tus códigos de respaldo</h3>
        <p className="tenue">
          Son la única forma de entrar si pierdes el teléfono. Cada uno sirve una sola vez y no se
          vuelven a mostrar.
        </p>
        <ul className="codigos-respaldo">
          {respaldo.map((c) => (
            <li key={c}>
              <code>{c}</code>
            </li>
          ))}
        </ul>
        <button
          onClick={() => {
            void navigator.clipboard?.writeText(respaldo.join('\n'));
          }}
        >
          Copiar todos
        </button>
        <button className="secundario" onClick={() => setPaso('inicio')}>
          Ya los guardé
        </button>
      </div>
    );
  }

  if (paso === 'confirmar' && alta) {
    return (
      <div className="bloque-seguridad">
        <h3>Escaneá el código</h3>
        <p className="tenue">
          Con Google Authenticator, Authy, 1Password o la que uses. Después ingresa el código que
          muestra, para confirmar que funciona antes de activarlo.
        </p>
        <img className="qr" src={alta.qr} alt="Código QR para la aplicación de autenticación" />
        <p className="tenue">
          ¿No puedes escanear? Ingresa esta clave a mano: <code>{alta.secreto}</code>
        </p>

        <form onSubmit={(e) => void activar(e)}>
          <label>
            <span>Código de la aplicación</span>
            <input
              ref={primerCampo}
              type="text"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              inputMode="numeric"
              placeholder="123456"
              required
            />
          </label>
          {error && <p className="error">{error}</p>}
          <div className="acciones">
            <button type="button" className="secundario" onClick={() => setPaso('inicio')}>
              Cancelar
            </button>
            <button type="submit" disabled={ocupado}>
              {ocupado ? 'Verificando…' : 'Activar'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (paso === 'desactivar') {
    return (
      <div className="bloque-seguridad">
        <h3>Desactivar la verificación en dos pasos</h3>
        <p className="tenue">
          Hacen falta tu contraseña y un código: quitar una protección no debería depender de una
          sola cosa.
        </p>
        <form onSubmit={(e) => void desactivar(e)}>
          <label>
            <span>Contraseña</span>
            <input
              ref={primerCampo}
              type="password"
              value={contrasena}
              onChange={(e) => setContrasena(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          <label>
            <span>Código de la aplicación</span>
            <input
              type="text"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              inputMode="numeric"
              placeholder="123456"
              required
            />
          </label>
          {error && <p className="error">{error}</p>}
          <div className="acciones">
            <button type="button" className="secundario" onClick={() => setPaso('inicio')}>
              Cancelar
            </button>
            <button type="submit" disabled={ocupado}>
              {ocupado ? 'Verificando…' : 'Desactivar'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="bloque-seguridad">
      <h3>Verificación en dos pasos</h3>
      {activo ? (
        <>
          <p className="estado activo">Activa</p>
          <p className="tenue">
            Te quedan {codigosRestantes} {codigosRestantes === 1 ? 'código' : 'códigos'} de respaldo.
          </p>
          <button className="secundario" onClick={() => setPaso('desactivar')}>
            Desactivar
          </button>
        </>
      ) : (
        <>
          <p className="estado inactiva">Inactiva</p>
          <p className="tenue">
            Con la contraseña sola alcanza para entrar. Un segundo factor protege la cuenta aunque
            la contraseña se filtre.
          </p>
          {error && <p className="error">{error}</p>}
          <button onClick={() => void iniciar()} disabled={ocupado}>
            {ocupado ? 'Preparando…' : 'Activar'}
          </button>
        </>
      )}
    </div>
  );
}
